import React from "react";

const ACCENT = "#1b4d2e";

// Retícula de 12 mini-meses, compartida por la vista Año del calendario y por
// el tab Calendario de leads (antes cada una tenía su propia copia del mismo
// grid).
//
// A 24px por día no caben carriles como en la vista de mes: lo único que se
// puede leer de un vistazo es CUÁNTO está ocupado, no quién. Por eso el
// contrato del día es una FRACCIÓN (`fill`, 0..1) que se pinta como un nivel
// que sube desde abajo. Con un solo espacio la fracción es 0 o 1 y se ve como
// siempre; con seis, un día con dos salones tomados se ve a un tercio — que
// es justo la diferencia que el rojo binario borraba.
//
// `dayInfo(iso)` lo provee cada consumidor y decide la semántica:
//   { fill, color, accent?, count?, tooltip?, clickable? }
// `accent` pinta una esquina (leads interesados), `count` un globito.

const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

// Lunes-first, igual que la vista de mes (locale `es` de FullCalendar).
const DOW = ["L", "M", "M", "J", "V", "S", "D"];

const pad = (n) => String(n).padStart(2, "0");
const isoDe = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// Lunes = 0 … Domingo = 6
const offsetLunes = (y, m) => (new Date(y, m, 1).getDay() + 6) % 7;

export default function MiniYearCalendar({
  year,
  dayInfo = () => null,
  onDayClick,
  onDayEnter,
  onDayLeave,
  onMonthClick,
}) {
  const hoy = new Date();
  const hoyIso = isoDe(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {Array.from({ length: 12 }).map((_, month) => {
        const dias = new Date(year, month + 1, 0).getDate();
        const offset = offsetLunes(year, month);
        return (
          <div
            key={`${year}-${month}`}
            className={`overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-700/60 dark:bg-zinc-800/50 ${
              onMonthClick
                ? "cursor-pointer transition-all duration-150 hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-md dark:hover:border-zinc-600"
                : ""
            }`}
            onClick={onMonthClick ? () => onMonthClick(year, month) : undefined}
          >
            <div
              className="px-3 py-2 text-center text-sm font-semibold uppercase tracking-wide"
              style={{ backgroundColor: ACCENT + "14", color: ACCENT }}
            >
              {MONTHS_ES[month]}
            </div>
            <div className="p-3">
              <div className="grid grid-cols-7 gap-1 text-xs">
                {DOW.map((d, i) => (
                  <div
                    key={`dow-${i}`}
                    className="text-center font-medium text-zinc-400 dark:text-zinc-500"
                  >
                    {d}
                  </div>
                ))}
                {Array.from({ length: offset }).map((_, i) => (
                  <div key={`pad-${month}-${i}`} />
                ))}
                {Array.from({ length: dias }).map((_, idx) => {
                  const dia = idx + 1;
                  const iso = isoDe(year, month, dia);
                  const info = dayInfo(iso) || null;
                  const fill = Math.max(0, Math.min(1, info?.fill ?? 0));
                  const esHoy = iso === hoyIso;
                  const lleno = fill >= 1;
                  const hayDato = !!info && (fill > 0 || info.accent || info.count);

                  // Nivel que sube desde abajo. El corte duro (sin degradado)
                  // es lo que deja leer la fracción a este tamaño.
                  const style = {};
                  if (fill > 0) {
                    const pct = Math.round((1 - fill) * 100);
                    style.background = `linear-gradient(to top, ${info.color} ${100 - pct}%, transparent ${100 - pct}%)`;
                  }
                  if (esHoy) style["--tw-ring-color"] = ACCENT;

                  return (
                    <div
                      key={`d-${month}-${dia}`}
                      // Con popover propio, el title nativo sale ENCIMA un
                      // segundo después y estorba: solo se usa donde no hay.
                      title={info?.clickable ? undefined : info?.tooltip}
                      onClick={
                        info?.clickable && onDayClick
                          ? (e) => {
                              e.stopPropagation();
                              onDayClick(iso, e.currentTarget.getBoundingClientRect());
                            }
                          : undefined
                      }
                      onMouseEnter={
                        info?.clickable && onDayEnter
                          ? (e) =>
                              onDayEnter(iso, e.currentTarget.getBoundingClientRect())
                          : undefined
                      }
                      onMouseLeave={info?.clickable && onDayLeave ? onDayLeave : undefined}
                      className={`relative flex h-6 w-6 items-center justify-center overflow-hidden rounded text-center ${
                        esHoy ? "ring-2 ring-offset-1 dark:ring-offset-zinc-800" : ""
                      } ${
                        info?.clickable
                          ? "cursor-pointer hover:ring-1 hover:ring-zinc-400"
                          : ""
                      } ${
                        // Un día sin NADA libre se delinea: es el estado que
                        // el dueño busca en un vistazo.
                        lleno ? "ring-1 ring-inset ring-black/10" : ""
                      }`}
                      style={style}
                    >
                      {/* Marca secundaria (leads interesados): esquina, para
                          no pelearse con el nivel de ocupación. */}
                      {info?.accent && (
                        <span
                          className="absolute right-0 top-0 h-0 w-0"
                          style={{
                            borderTop: `7px solid ${info.accent}`,
                            borderLeft: "7px solid transparent",
                          }}
                        />
                      )}
                      <span
                        className={`relative z-10 text-[11px] leading-none ${
                          lleno
                            ? "font-semibold text-white"
                            : hayDato
                              ? "font-medium text-zinc-800 dark:text-zinc-100"
                              : esHoy
                                ? "font-bold text-zinc-700 dark:text-zinc-200"
                                : "text-zinc-600 dark:text-zinc-400"
                        }`}
                      >
                        {dia}
                      </span>
                      {info?.count > 1 && (
                        <span
                          className="absolute -right-0.5 -top-0.5 z-20 flex h-3 w-3 items-center justify-center rounded-full text-[7px] font-bold text-white"
                          style={{ backgroundColor: ACCENT }}
                        >
                          {info.count}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
